/**
 * What-If Scenario Modelling Widget
 * ===================================
 * "What if I reduce food cost by 2%?" → shows projected P&L impact
 * Interactive sliders for key business variables with real-time P&L projection
 */

import React, { useState, useMemo } from 'react';
import { Sliders, TrendingUp, TrendingDown, DollarSign, ArrowRight, RotateCcw, Database, Zap } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { formatCurrency, formatPercent } from '@/utils/formatters';

// Industry-specific scenario configurations
const SCENARIOS = {
  hospitality: {
    baseRevenue: 185420,
    baseCosts: { cogs: 28, labor: 32, rent: 10, marketing: 3, other: 5 },
    variables: [
      { id: 'cogs', label: 'Food Cost %', current: 28, min: 20, max: 40, step: 0.5, unit: '%', type: 'cost', source: 'Xero + POS' },
      { id: 'labor', label: 'Labour Cost %', current: 32, min: 22, max: 42, step: 0.5, unit: '%', type: 'cost', source: 'Deputy + Xero' },
      { id: 'revenue_growth', label: 'Revenue Growth', current: 0, min: -20, max: 30, step: 1, unit: '%', type: 'revenue', source: 'POS Forecast' },
      { id: 'covers', label: 'Cover Volume Change', current: 0, min: -30, max: 30, step: 1, unit: '%', type: 'revenue', source: 'Reservation System' },
      { id: 'marketing', label: 'Marketing Spend %', current: 3, min: 0, max: 10, step: 0.5, unit: '%', type: 'cost', source: 'Xero' },
    ]
  },
  retail: {
    baseRevenue: 245680,
    baseCosts: { cogs: 60, labor: 14, rent: 10, marketing: 5, other: 3 },
    variables: [
      { id: 'cogs', label: 'COGS %', current: 60, min: 40, max: 75, step: 0.5, unit: '%', type: 'cost', source: 'Xero + POS' },
      { id: 'labor', label: 'Wage Cost %', current: 14, min: 8, max: 25, step: 0.5, unit: '%', type: 'cost', source: 'Deputy + Xero' },
      { id: 'revenue_growth', label: 'Revenue Growth', current: 0, min: -20, max: 30, step: 1, unit: '%', type: 'revenue', source: 'POS Forecast' },
      { id: 'conversion', label: 'Conversion Rate Change', current: 0, min: -15, max: 30, step: 1, unit: '%', type: 'revenue', source: 'POS + Traffic Counter' },
      { id: 'marketing', label: 'Marketing Spend %', current: 5, min: 0, max: 15, step: 0.5, unit: '%', type: 'cost', source: 'Xero' },
    ]
  },
  professional: {
    baseRevenue: 312500,
    baseCosts: { labor: 60, rent: 10, software: 5, marketing: 3, other: 5 },
    variables: [
      { id: 'labor', label: 'Staff Cost %', current: 60, min: 40, max: 70, step: 0.5, unit: '%', type: 'cost', source: 'Xero Payroll' },
      { id: 'utilization', label: 'Utilisation Change', current: 0, min: -15, max: 20, step: 1, unit: '%', type: 'revenue', source: 'Time Tracking' },
      { id: 'revenue_growth', label: 'Revenue Growth', current: 0, min: -20, max: 30, step: 1, unit: '%', type: 'revenue', source: 'Pipeline Forecast' },
      { id: 'recovery', label: 'Recovery Rate Change', current: 0, min: -10, max: 15, step: 1, unit: '%', type: 'revenue', source: 'Practice Mgmt' },
      { id: 'marketing', label: 'BD Spend %', current: 3, min: 0, max: 10, step: 0.5, unit: '%', type: 'cost', source: 'Xero' },
    ]
  },
};

export const WhatIfScenario = ({ industry = 'hospitality', className = '' }) => {
  const config = SCENARIOS[industry] || SCENARIOS.hospitality;
  
  // State for each variable
  const [adjustments, setAdjustments] = useState(
    Object.fromEntries(config.variables.map(v => [v.id, v.current]))
  );
  
  const updateVariable = (id, value) => {
    setAdjustments(prev => ({ ...prev, [id]: value[0] }));
  };
  
  const resetAll = () => {
    setAdjustments(Object.fromEntries(config.variables.map(v => [v.id, v.current])));
  };
  
  // Calculate projected P&L
  const projectedPL = useMemo(() => {
    let projectedRevenue = config.baseRevenue;
    let totalCostPct = 0;
    
    config.variables.forEach(v => {
      const adjusted = adjustments[v.id];
      if (v.type === 'revenue' && v.id !== 'cogs' && v.id !== 'labor' && v.id !== 'marketing') {
        const growthPct = adjusted / 100;
        projectedRevenue = projectedRevenue * (1 + growthPct);
      }
      if (v.type === 'cost') {
        totalCostPct += adjusted;
      }
    });
    
    // Add fixed costs that weren't adjusted
    Object.entries(config.baseCosts).forEach(([key, val]) => {
      const variable = config.variables.find(v => v.id === key);
      if (!variable) {
        totalCostPct += val;
      }
    });
    
    const totalExpenses = projectedRevenue * (totalCostPct / 100);
    const netProfit = projectedRevenue - totalExpenses;
    const netMargin = (netProfit / projectedRevenue) * 100;
    
    // Compare to base
    const baseExpensePct = Object.values(config.baseCosts).reduce((s, v) => s + v, 0);
    const baseNetProfit = config.baseRevenue * (1 - baseExpensePct / 100);
    const baseNetMargin = ((config.baseRevenue - config.baseRevenue * baseExpensePct / 100) / config.baseRevenue) * 100;
    
    return {
      revenue: projectedRevenue,
      expenses: totalExpenses,
      netProfit,
      netMargin,
      baseRevenue: config.baseRevenue,
      baseNetProfit,
      baseNetMargin,
      profitDelta: netProfit - baseNetProfit,
      marginDelta: netMargin - baseNetMargin,
    };
  }, [adjustments, config]);
  
  const hasChanges = config.variables.some(v => adjustments[v.id] !== v.current);

  return (
    <div className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-6 ${className}`} data-testid="what-if-widget">
      <div className="absolute -top-20 -left-20 w-56 h-56 bg-gradient-to-br from-violet-100/40 to-purple-200/30 rounded-full blur-3xl pointer-events-none" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
              <Sliders className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 tracking-tight">What-If Scenarios</h3>
              <p className="text-xs text-gray-500">Adjust variables to see projected P&L impact</p>
            </div>
          </div>
          {hasChanges && (
            <button onClick={resetAll} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-full transition-colors border border-gray-200" data-testid="reset-scenario">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )}
        </div>

        {/* Variable Sliders */}
        <div className="space-y-4 mb-6">
          {config.variables.map(variable => {
            const currentVal = adjustments[variable.id];
            const hasChanged = currentVal !== variable.current;
            const delta = currentVal - variable.current;
            
            return (
              <div key={variable.id} className={`p-3 rounded-xl transition-all ${hasChanged ? 'bg-violet-50/50 border border-violet-100' : 'bg-gray-50/50 border border-gray-100/50'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{variable.label}</span>
                    {hasChanged && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        (variable.type === 'cost' && delta < 0) || (variable.type === 'revenue' && delta > 0)
                          ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                          : 'bg-red-100 text-red-700 border border-red-200'
                      }`}>
                        {delta > 0 ? '+' : ''}{delta.toFixed(1)}{variable.unit}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold ${hasChanged ? 'text-violet-700' : 'text-gray-900'}`}>
                      {currentVal.toFixed(1)}{variable.unit}
                    </span>
                    <Database className="w-2.5 h-2.5 text-gray-300" title={variable.source} />
                  </div>
                </div>
                <Slider
                  value={[currentVal]}
                  onValueChange={(val) => updateVariable(variable.id, val)}
                  min={variable.min}
                  max={variable.max}
                  step={variable.step}
                  className="w-full"
                />
                <div className="flex justify-between mt-1">
                  <span className="text-[9px] text-gray-400">{variable.min}{variable.unit}</span>
                  <span className="text-[9px] text-gray-400">{variable.max}{variable.unit}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Projected Results */}
        <div className={`p-4 rounded-2xl border transition-all ${
          projectedPL.profitDelta >= 0 
            ? 'bg-gradient-to-br from-emerald-50/80 to-blue-50/50 border-emerald-200' 
            : 'bg-gradient-to-br from-red-50/80 to-rose-50/50 border-red-200'
        }`}>
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-violet-500" />
            <span className="text-sm font-semibold text-gray-800">Projected Impact</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Revenue */}
            <div className="text-center p-2 rounded-xl bg-white/60">
              <p className="text-[10px] text-gray-500 font-medium mb-1">Revenue MTD</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(projectedPL.revenue)}</p>
              {projectedPL.revenue !== projectedPL.baseRevenue && (
                <p className={`text-[10px] font-bold mt-0.5 ${projectedPL.revenue > projectedPL.baseRevenue ? 'text-emerald-600' : 'text-red-600'}`}>
                  {projectedPL.revenue > projectedPL.baseRevenue ? '+' : ''}{formatCurrency(projectedPL.revenue - projectedPL.baseRevenue)}
                </p>
              )}
            </div>
            
            {/* Net Profit */}
            <div className="text-center p-2 rounded-xl bg-white/60">
              <p className="text-[10px] text-gray-500 font-medium mb-1">Net Profit</p>
              <p className={`text-lg font-bold ${projectedPL.netProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatCurrency(projectedPL.netProfit)}</p>
              {hasChanges && (
                <p className={`text-[10px] font-bold mt-0.5 ${projectedPL.profitDelta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {projectedPL.profitDelta >= 0 ? '+' : ''}{formatCurrency(projectedPL.profitDelta)}
                </p>
              )}
            </div>
            
            {/* Margin */}
            <div className="text-center p-2 rounded-xl bg-white/60">
              <p className="text-[10px] text-gray-500 font-medium mb-1">Net Margin</p>
              <p className={`text-lg font-bold ${projectedPL.netMargin >= projectedPL.baseNetMargin ? 'text-emerald-700' : 'text-red-700'}`}>{projectedPL.netMargin.toFixed(1)}%</p>
              {hasChanges && (
                <p className={`text-[10px] font-bold mt-0.5 ${projectedPL.marginDelta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {projectedPL.marginDelta >= 0 ? '+' : ''}{projectedPL.marginDelta.toFixed(1)}pts
                </p>
              )}
            </div>
          </div>
          
          {hasChanges && (
            <div className="mt-3 pt-3 border-t border-white/50">
              <p className="text-[10px] text-gray-500 flex items-center gap-1">
                <Database className="w-3 h-3" />
                Projections based on current MTD data from connected integrations. Results are estimates.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatIfScenario;
